import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SeasonStatus = "pre-season" | "in-season" | "post-season";

export interface HomepageContent {
  badge: { enabled: boolean; text: string; href: string };
  hero: { eyebrow: string; title: string; subtitle: string; description: string };
  primaryCta: { enabled: boolean; label: string; href: string };
  secondaryCta: { enabled: boolean; label: string; href: string };
  tertiaryCta: { enabled: boolean; label: string; href: string };
  bottomBanner: {
    enabled: boolean;
    title: string;
    description: string;
    primaryLabel: string;
    primaryHref: string;
    secondaryLabel: string;
    secondaryHref: string;
  };
  seasonStatus: SeasonStatus;
  offSeasonMessage: string;
}

export const DEFAULT_HOMEPAGE_CONTENT: HomepageContent = {
  badge: {
    enabled: true,
    text: "Fall 2026 Season Is Underway",
    href: "/schedules",
  },
  hero: {
    eyebrow: "Bad Axe",
    title: "Back to the Roots",
    subtitle: "Flag Football",
    description:
      "Building teamwork, sportsmanship, and healthy lifestyles through youth flag football in Bad Axe, Michigan. Join our community of young athletes.",
  },
  primaryCta: { enabled: true, label: "View Game Schedules", href: "/schedules" },
  secondaryCta: { enabled: true, label: "Learn More About Our League", href: "/about" },
  tertiaryCta: { enabled: true, label: "Find My Player", href: "/my-team" },
  bottomBanner: {
    enabled: true,
    title: "Fall 2026 Is Underway",
    description:
      "Registration is closed and games are underway. Follow schedules, scores, and standings throughout the season.",
    primaryLabel: "View Schedules",
    primaryHref: "/schedules",
    secondaryLabel: "View Standings",
    secondaryHref: "/standings",
  },
  seasonStatus: "in-season",
  offSeasonMessage: "Fall games are under way — check schedules for game times!",
};

export function useHomepageContent() {
  return useQuery({
    queryKey: ["site_settings", "homepage"],
    queryFn: async (): Promise<HomepageContent> => {
      const { data } = await supabase
        .from("site_settings" as any)
        .select("value")
        .eq("key", "homepage")
        .maybeSingle();
      const value = (data as any)?.value as Partial<HomepageContent> | undefined;
      if (!value) return DEFAULT_HOMEPAGE_CONTENT;
      return { ...DEFAULT_HOMEPAGE_CONTENT, ...value } as HomepageContent;
    },
    staleTime: 60_000,
  });
}
